import { Component, Input, OnInit, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ComponentService, CreatorFrontApi } from '../../../services/component.service';
import { OverlayService } from '../../../services/overlay.service';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { tap, filter, debounceTime } from 'rxjs/operators';
import { FontsService, Font } from '../../../services/fonts.service';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from '../../../services/auth.service';

@Component({
	selector: 'app-barcode',
	templateUrl: './barcode.component.html',
	styleUrls: ['./barcode.component.scss']
})
export class BarcodeComponent implements OnInit, OnDestroy {
	@Input() componentData: any;
	@Input() formHeight: string;
	@Input() pageType: string;
	@Input() componentID: number;
	@Input() urlGlobalRegExp: any;
	@Input() emailRegExp: any;
	@ViewChild('barcodeForm', { static: true }) barcodeForm: NgForm;
	@ViewChild('nameField', { static: true }) nameField;
	barcodeFormSubscription: Subscription;
	defaultChanged = false;
	isAdmin;
	font;
	fonts: Array<Font>;
	emailError;
	emailRemovalError = false;
	dbCountTotal;
	dbCountAssigned;
	dbCountAvailable;
	couponCountSubscription;
	barcodeTypes = [
		{
			type: 'code128',
			label: 'Code 128'
		},
		{
			type: 'upca',
			label: 'UPC-A'
		},
		{
			type: 'upcacomposite',
			label: 'UPC-A Composite'
		},
		{
			type: 'upce',
			label: 'UPC-E'
		},
		{
			type: 'upcecomposite',
			label: 'UPC-E Composite'
		},
		{
			type: 'ean13',
			label: 'EAN-13'
		},
		{
			type: 'ean13composite',
			label: 'EAN-13 Composite'
		},
		{
			type: 'ean14',
			label: 'GS1-14'
		},
		{
			type: 'ean2',
			label: 'EAN-2 (2 digit addon)'
		},
		{
			type: 'ean5',
			label: 'EAN-5 (5 digit addon)'
		},
		{
			type: 'ean8',
			label: 'EAN-8'
		},
		{
			type: 'ean8composite',
			label: 'EAN-8 Composite'
		},
		{
			type: 'code39',
			label: 'Code 39'
		},
		{
			type: 'code39ext',
			label: 'Code 39 Extended'
		},
		{
			type: 'itf14',
			label: 'ITF-14'
		},
		{
			type: 'msi',
			label: 'MSI Modified Plessey'
		}
	];

	constructor(
		private comService: ComponentService,
		private overlay: OverlayService,
		private fontsService: FontsService,
		private sanitation: DomSanitizer,
		private auth: AuthService,
		private cd: ChangeDetectorRef
	) {}

	ngOnInit() {
		this.isAdmin = this.auth.isAdmin();
		this.dbCountTotal =
			this.dbCountAssigned =
			this.dbCountAvailable =
				this.sanitation.bypassSecurityTrustHtml('<i class="fas fa-sync fa-spin" style="font-size:90%;color:#212529;"></i>');

		if (this.componentID != -1) {
			this.checkCoupons();
		}

		if (this.isAdmin || this.componentData.config.remove) {
			this.componentData.config.remove = {
				csv: '',
				include_assigned: false,
				notify_removal_only: false,
				email: ''
			};
		}

		this.emailError = this.componentData.config.email ? false : true;
		this.fonts = this.fontsService.getFonts();

		this.fonts.unshift({
			key: 13,
			label: 'Default Barcode Font',
			url: 'Default',
			type: 'default'
		});

		this.font =
			this.fontsService.getFontsArray().indexOf(this.componentData.config.text.font) === -1 &&
			!(this.componentData.config.text.font == 'Default')
				? 'Custom Font'
				: this.componentData.config.text.font;

		this.barcodeFormSubscription = this.barcodeForm.valueChanges
			.pipe(
				tap(() =>
					this.comService.updateDisabledSave(
						(this.barcodeForm.dirty && this.barcodeForm.invalid) || this.nameField.editComponentName
					)
				),
				filter((form) => this.barcodeForm.dirty && this.barcodeForm.valid),
				debounceTime(2000)
			)
			.subscribe((data) => {
				this.defaultChanged = true;
			});
	}

	ngOnDestroy() {
		this.barcodeFormSubscription.unsubscribe();
		if (this.couponCountSubscription) {
			this.couponCountSubscription.unsubscribe();
		}
	}

	brodcastToPreview() {
		this.comService.updateComponentData(this.componentData);
		this.comService.updatePreviewData();
	}

	addCSV() {
		if (this.componentData.config.csv_list.length < 20) {
			this.componentData.config.csv_list.push('');
		} else {
			this.overlay.updateOverlay(true, 'You have reached the limit of 20 CSV files', 'alert');
		}
	}

	removeCSV(index) {
		this.componentData.config.csv_list.splice(index, 1);
	}

	emailCheck() {
		const emails = this.componentData.config.email.split(',').map((item) => item.trim());
		this.emailError = false;
		for (const email of emails) {
			const valid = this.emailRegExp.test(email);
			if (!valid) {
				this.emailError = true;
			}
		}
	}

	emailRemovalCheck() {
		const valid = this.emailRegExp.test(this.componentData.config.remove.email);
		if (!valid) {
			this.emailRemovalError = true;
		}
	}

	checkCoupons() {
		const data = {
			name: this.componentData.name,
			legacy: this.componentData.legacy,
			config: JSON.stringify(this.componentData.config),
			data: JSON.stringify({ widgetId: this.componentID })
		};

		this.couponCountSubscription = this.comService.checkCouponCounts(data).subscribe(
			(response: CreatorFrontApi) => {
				if (response && response.status) {
					if (response.data) {
						this.dbCountTotal = response.data.total;
						this.dbCountAvailable = response.data.available;
						this.dbCountAssigned = this.dbCountTotal - this.dbCountAvailable;
					} else {
						this.dbCountTotal = 'n/a';
						this.dbCountAssigned = 'n/a';
						this.dbCountAvailable = 'n/a';
					}
					this.cd.detectChanges();
				}
			},
			(err) => {
				this.dbCountTotal = 'n/a';
				this.dbCountAssigned = 'n/a';
				this.dbCountAvailable = 'n/a';
				this.cd.detectChanges();
			}
		);
	}
}
